import { Button, Form, Input, App as AntApp } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useAuthStore } from '@/shared/auth/auth-store';
import { loginRequest } from '../api/login';
import { asApiError } from '@/shared/lib/api';
import s from './login-page.module.css';

type FormValues = {
  username: string;
  password: string;
};

const LANGS = ['ru', 'uz', 'en'] as const;

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { message } = AntApp.useApp();
  const [submitting, setSubmitting] = useState(false);

  async function onFinish(values: FormValues) {
    setSubmitting(true);
    try {
      const { accessToken, user } = await loginRequest(values);
      setSession(accessToken, user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const apiErr = asApiError(err);
      const text =
        apiErr.code === 'UNAUTHENTICATED'
          ? t('auth.login.invalidCredentials')
          : apiErr.code === 'NETWORK_ERROR'
            ? t('auth.login.networkError')
            : apiErr.message;
      void message.error(text);
    } finally {
      setSubmitting(false);
    }
  }

  const activeLng = (i18n.resolvedLanguage ?? 'ru').slice(0, 2);

  return (
    <div className={s.shell}>
      <aside className={s.hero} aria-hidden="true">
        <img src="/login-hero.png" alt="" className={s.heroImg} />
      </aside>

      <main className={s.panel}>
        <div className={s.topBar}>
          <div className={s.brandMark}>
            <img src="/pop-garden-logo.png" alt="Pop Garden" style={{ height: 40, width: 'auto' }} />
          </div>
          <div className={s.langs} role="group" aria-label={t('auth.login.languageGroup')}>
            {LANGS.map((lng) => (
              <button
                key={lng}
                type="button"
                onClick={() => void i18n.changeLanguage(lng)}
                className={`${s.lang} ${activeLng === lng ? s.langActive : ''}`}
                aria-pressed={activeLng === lng}
              >
                {lng}
              </button>
            ))}
          </div>
        </div>

        <div className={s.panelInner}>
          <header className={s.header}>
            <h1 className={s.title}>{t('auth.login.title')}</h1>
            <p className={s.subtitle}>{t('auth.login.subtitle')}</p>
          </header>

          <Form<FormValues>
            className={s.form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
            requiredMark={false}
            initialValues={{ username: '', password: '' }}
          >
            <Form.Item
              name="username"
              label={t('auth.login.username')}
              rules={[{ required: true, message: t('auth.login.usernameRequired') }]}
            >
              <Input
                autoFocus
                autoComplete="username"
                placeholder={t('auth.login.usernamePlaceholder')}
              />
            </Form.Item>

            <div className={s.passwordHeader}>
              <label htmlFor="login-password">{t('auth.login.password')}</label>
              <button
                type="button"
                className={s.forgot}
                onClick={() =>
                  void message.info(t('auth.login.forgotPasswordHint'))
                }
              >
                {t('auth.login.forgotPassword')}
              </button>
            </div>
            <Form.Item
              name="password"
              className={s.passwordItem}
              rules={[{ required: true, message: t('auth.login.passwordRequired') }]}
            >
              <Input.Password
                id="login-password"
                autoComplete="current-password"
                placeholder={t('auth.login.passwordPlaceholder')}
              />
            </Form.Item>

            <Form.Item className={s.submitWrap}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={submitting}
                className={s.submit}
              >
                {submitting ? t('auth.login.loggingIn') : t('auth.login.submit')}
              </Button>
            </Form.Item>
          </Form>

          <div className={s.helper}>
            {t('auth.login.needAccess')}{' '}
            <a className={s.helperLink} href="mailto:admin@popgarden.uz">
              {t('auth.login.contactAdmin')}
            </a>
          </div>
        </div>

        <div className={s.meta}>
          <span>© Pop Garden {new Date().getFullYear()}</span>
          <div className={s.metaLinks}>
            <a href="#" className={s.metaLink}>
              {t('auth.login.terms')}
            </a>
            <a href="#" className={s.metaLink}>
              {t('auth.login.privacy')}
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
